import { inject, Injectable, signal } from '@angular/core';
import Keycloak from '@inoventoryapps/keycloak-js';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { APPLICATION_ROLES } from '../../constants/application-roles.constant';
import { EXCLUDED_RESOURCES } from '../../constants/excluded-resources.constant';

import { APP_LANGUAGES, DEFAULT_LANGUAGE } from '../../constants/global-header.constant';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { Roles } from '../../enums/roles.enum';
import { IClientIdMapping, IGeoLocationMapping } from '../../models/cdn.model';
import { IKeycloakUserInfo, IResourceAccess } from '../../models/keycloak.model';
import {
    IAppInitializationData,
    IApplicationPermission,
    IBlockPermission,
    IDistrictPermission,
    INationalPermission,
    IStatePermission,
    IUserProfile,
} from '../../models/login.model';
import { CdnService } from '../cdn/cdn.service';
import { mobileBridge } from '../mobile/mobile-bridge';
import { SnackbarService } from '../snackbar/snackbar.service';
import { StorageService } from '../storage/storage-service';
import { UserService } from '../user/user.service';

interface IGeoPermissionCollectors {
    applicationPermissions: IApplicationPermission[];
    blockPermissions: IBlockPermission[];
    districtPermissions: IDistrictPermission[];
    setNational: (value: INationalPermission[]) => void;
    statePermissions: IStatePermission[];
}

@Injectable({
    providedIn: 'root',
})
export class AppInitializationService {
    private readonly cdnService = inject(CdnService);
    private readonly keycloak = inject(Keycloak);
    private readonly snackbarService = inject(SnackbarService);
    private readonly storageService = inject(StorageService);
    private readonly translate = inject(TranslateService);
    private readonly userService = inject(UserService);

    // State storage for holding initialization data across the application
    private readonly _clientIdsMapping = signal<IClientIdMapping[]>([]);
    private readonly _geoLocationMapping = signal<IGeoLocationMapping[]>([]);
    private readonly _profileImage = signal<string>('');
    private readonly _subRoles = signal<string[]>([]);
    private _isInitialized = false;
    private initializationPromise: Promise<IAppInitializationData> | null = null;

    readonly clientIdsMapping = this._clientIdsMapping.asReadonly();
    readonly geoLocationMapping = this._geoLocationMapping.asReadonly();
    readonly profileImage = this._profileImage.asReadonly();
    readonly subRoles = this._subRoles.asReadonly();

    get isInitialized(): boolean {
        return this._isInitialized;
    }

    getClientIdsMapping(): IClientIdMapping[] {
        return this._clientIdsMapping();
    }

    getGeoLocationMapping(): IGeoLocationMapping[] {
        return this._geoLocationMapping();
    }

    getProfileImage(): string {
        return this._profileImage();
    }

    getSubRoles(): string[] {
        return this._subRoles();
    }

    async initializeAppData(): Promise<IAppInitializationData> {
        if (this._isInitialized) {
            return {
                clientIdsMapping: this._clientIdsMapping(),
                geoLocationMapping: this._geoLocationMapping(),
                profileImage: this._profileImage(),
                subRoles: this._subRoles(),
            };
        }

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = (async () => {
            const sub = this.keycloak.tokenParsed?.sub ?? '';

            const [clientIdsMapping, geoLocationMapping, profileImage, subRoles] = await Promise.all([
                firstValueFrom(this.cdnService.getClientIdsMapping()).catch(() => [] as IClientIdMapping[]),
                firstValueFrom(this.cdnService.getGeoLocationMapping()).catch(() => [] as IGeoLocationMapping[]),
                firstValueFrom(this.userService.getUserProfileImage()).catch(() => ''),
                firstValueFrom(this.userService.getSubRoles(sub)).catch(() => [] as string[]),
            ]);

            this._clientIdsMapping.set(clientIdsMapping);
            this._geoLocationMapping.set(geoLocationMapping);
            this._profileImage.set(profileImage);
            this._subRoles.set(subRoles);
            this._isInitialized = true;

            if (subRoles.length) {
                this.storageService.setLocalItem<string[]>(LOCAL_STORAGE_KEYS.SUB_ROLES, subRoles);
            }

            const parsed = this.keycloak.tokenParsed as unknown as IKeycloakUserInfo | undefined;

            if (mobileBridge.isPWA()) {
                this.translate.addLangs(APP_LANGUAGES.map((lang) => lang.langCode));
                this.translate.setFallbackLang(DEFAULT_LANGUAGE.langCode);
                this.translate.use(this.resolveLanguage(mobileBridge.getLanguage()));

                if (parsed) {
                    const bridgeRole = mobileBridge.getRole();
                    this.setupUserData(parsed.resource_access ?? {}, geoLocationMapping);

                    if (bridgeRole) {
                        this.storageService.setLocalItem<string>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE, bridgeRole);
                        this.userService.setActiveRole(bridgeRole);
                    }

                    if (subRoles.length) {
                        this.storageService.setLocalItem<string[]>(LOCAL_STORAGE_KEYS.SUB_ROLES, subRoles);
                    }

                    const existingRoles =
                        this.storageService.getLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES) ?? [];
                    const combinedRoles = Array.from(new Set<string>([...existingRoles, ...subRoles]));
                    this.storageService.setLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES, combinedRoles);

                    const userProfile: IUserProfile = this.buildUserProfile(parsed, profileImage);
                    this.storageService.setLocalItem<IUserProfile>(LOCAL_STORAGE_KEYS.USER_PROFILE, userProfile);
                }
            } else if (parsed) {
                const activeRole = this.setupUserData(parsed.resource_access ?? {}, geoLocationMapping);
                if (activeRole) {
                    const userProfile: IUserProfile = this.buildUserProfile(parsed, profileImage);
                    this.storageService.setLocalItem<IUserProfile>(LOCAL_STORAGE_KEYS.USER_PROFILE, userProfile);
                }
            }

            return {
                clientIdsMapping,
                geoLocationMapping,
                profileImage,
                subRoles,
            };
        })();

        return this.initializationPromise;
    }

    setupUserData(resourceAccess: IResourceAccess, geoLocationMapping: IGeoLocationMapping[]): string | null {
        const storedActiveRole = this.storageService.getLocalItem<string>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE);
        this.storageService.clearLocal();

        const assignedRoles: string[] = [];
        const statePermissions: IStatePermission[] = [];
        const districtPermissions: IDistrictPermission[] = [];
        const blockPermissions: IBlockPermission[] = [];
        const applicationPermissions: IApplicationPermission[] = [];
        let nationalPermissions: INationalPermission[] = [];

        for (const [resource, entry] of Object.entries(resourceAccess)) {
            if (EXCLUDED_RESOURCES.has(resource)) {
                continue;
            }
            for (const role of entry?.roles ?? []) {
                this.collectGeoPermission(resource, role, geoLocationMapping, {
                    applicationPermissions,
                    blockPermissions,
                    districtPermissions,
                    setNational: (value) => (nationalPermissions = value),
                    statePermissions,
                });
                if (!assignedRoles.includes(role)) {
                    assignedRoles.push(role);
                }
            }
        }

        const availableRoles = APPLICATION_ROLES.filter((role) => assignedRoles.includes(role));
        const nextActiveRole =
            storedActiveRole && availableRoles.includes(storedActiveRole as Roles)
                ? storedActiveRole
                : (availableRoles[0] ?? null);

        if (!nextActiveRole) {
            return null;
        }

        this.storageService.setLocalItem<string>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE, nextActiveRole);
        this.storageService.setLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES, assignedRoles);
        this.storageService.setLocalItem<IStatePermission[]>(LOCAL_STORAGE_KEYS.STATE_PERMISSIONS, statePermissions);
        this.storageService.setLocalItem<INationalPermission[]>(
            LOCAL_STORAGE_KEYS.NATIONAL_PERMISSIONS,
            nationalPermissions,
        );
        this.storageService.setLocalItem<IDistrictPermission[]>(
            LOCAL_STORAGE_KEYS.DISTRICT_PERMISSIONS,
            districtPermissions,
        );
        this.storageService.setLocalItem<IBlockPermission[]>(LOCAL_STORAGE_KEYS.BLOCK_PERMISSIONS, blockPermissions);
        this.storageService.setLocalItem<IApplicationPermission[]>(
            LOCAL_STORAGE_KEYS.APPLICATION_PERMISSIONS,
            applicationPermissions,
        );
        this.userService.setActiveRole(nextActiveRole);

        return nextActiveRole;
    }

    buildUserProfile(parsed: IKeycloakUserInfo, profileImage: string): IUserProfile {
        return {
            sub: parsed.sub ?? '',
            resource_access: parsed.resource_access ?? ({} as IResourceAccess),
            profile: parsed.profile ?? '',
            name: parsed.name ?? '',
            email: parsed.email ?? '',
            profileImage,
        };
    }

    /**
     * Maps a language code from the native bridge onto one the app has a translation
     * file for. Hosts report codes in varied shapes (`en-IN`, `en_US`, `EN`), so only
     * the primary subtag is considered.
     * @param requested the raw code reported by the bridge
     * @returns a supported language code
     */
    private resolveLanguage(requested: string): string {
        const primarySubtag = (requested || '').toLowerCase().split(/[-_]/)[0];
        const supported = APP_LANGUAGES.some((language) => language.langCode === primarySubtag);

        if (!supported) {
            console.warn(
                `Unsupported language "${requested}" requested; falling back to "${DEFAULT_LANGUAGE.langCode}"`,
            );
        }

        return supported ? primarySubtag : DEFAULT_LANGUAGE.langCode;
    }

    private collectGeoPermission(
        key: string,
        role: string,
        geoLocationMapping: IGeoLocationMapping[],
        collectors: IGeoPermissionCollectors,
    ): void {
        try {
            const prefix = key.split('-')[0];
            let geoLocation: IGeoLocationMapping | undefined;

            switch (prefix) {
                case 'B':
                    geoLocation = geoLocationMapping.find((geoLocation) => geoLocation.block_id === key);
                    if (geoLocation) {
                        collectors.blockPermissions.push({
                            ...geoLocation,
                            state_id: +geoLocation.state_id.split('-')[1],
                            state_name_en: geoLocation.state_name,
                            state_short_name_en: geoLocation.short_code,
                            district_id: +geoLocation.district_id.split('-')[1],
                            district_name_en: geoLocation.district_name,
                            block_id: +geoLocation.block_id.split('-')[1],
                            block_name_en: geoLocation.block_name,
                            role,
                        });
                    }
                    break;
                case 'D':
                    geoLocation = geoLocationMapping.find((geoLocation) => geoLocation.district_id === key);
                    if (geoLocation) {
                        collectors.districtPermissions.push({
                            ...geoLocation,
                            state_id: +geoLocation.state_id.split('-')[1],
                            state_name_en: geoLocation.state_name,
                            state_short_name_en: geoLocation.short_code,
                            district_id: +geoLocation.district_id.split('-')[1],
                            district_name_en: geoLocation.district_name,
                            block_code: '',
                            block_name: '',
                            block_id: 0,
                            role,
                        });
                    }
                    break;
                case 'S':
                    geoLocation = geoLocationMapping.find(
                        (geoLocation) => geoLocation.state_id.split('-')[1].padStart(2, '0') === key.split('-')[1],
                    );
                    if (geoLocation) {
                        collectors.statePermissions.push({
                            ...geoLocation,
                            state_id: +geoLocation.state_id.split('-')[1],
                            state_name_en: geoLocation.state_name,
                            state_short_name_en: geoLocation.short_code,
                            district_code: '',
                            district_name: '',
                            district_id: 0,
                            block_code: '',
                            block_name: '',
                            block_id: 0,
                            role,
                        });
                    }
                    break;
                case 'N':
                    collectors.setNational(
                        geoLocationMapping
                            .filter(
                                (geoLocation, index, self) =>
                                    index === self.findIndex((x) => x.state_id === geoLocation.state_id),
                            )
                            .map((geoLocation) => ({
                                state_id: +geoLocation.state_id.split('-')[1],
                                state_name: geoLocation.state_name,
                                state_name_en: geoLocation.state_name,
                                state_short_name_en: geoLocation.short_code,
                            })),
                    );
                    break;
                default:
                    collectors.applicationPermissions.push({ appName: key, role });
            }
        } catch {
            this.snackbarService.errorSnackbar('Error while creating permissions for geoLocation');
        }
    }
}
